import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import dweLogo from "../../assets/images/webLogin/dweLogo.svg";
import playText from "../../assets/images/webLogin/play.svg";
import background from "../../assets/images/webLogin/background.png";
import eTabWelcome from "../../assets/images/webLogin/eTabWelcome.png";

import Loader from "../../components/ui/Loader";
import Text from "../../components/ui/Text";
import PillButton from "../../components/ui/PillButton";
import useStore from "../../hooks/store/useStore";
import changeLanguageByMarket from "../../locales/utils/changeLanguageByMarket";
import usePhoneNumberLogin from "../../services/webLogin/usePhoneNumberLogin";
import { LoginResponse } from "../../services/webLogin/types";
import importFont from "../../styles/utils/importFont";
import cn from "../../utils/cn";
import { MarketType } from "../../types";

import PhoneNumberInput from "./components/PhoneNumberInput";
import SmsDrawer from "./components/SmsDrawer";
import isValidPhoneNumber from "./utils/isValidPhoneNumber";
import openApp from "./utils/openApp";
import { CountDownTime } from "./constants";

export default function WebLogin() {
  const { t } = useTranslation();

  const [market, setMarket] = useState<MarketType>();
  const [fontLoaded, setFontLoaded] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showSmsDrawer, setShowSmsDrawer] = useState(false);
  const [smsCode, setSmsCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [isFirstFocused, setIsFirstFocused] = useState(false);

  const { language, setLanguage } = useStore((state) => state.global);

  const mutationForLogin = usePhoneNumberLogin();

  const handleSubmit = () => {
    if (!market) return setErrorMessage("Invalid market: " + market);

    if (!isValidPhoneNumber(phoneNumber, market))
      return setErrorMessage(t("invalidPhoneFormat"));

    mutationForLogin.mutate(phoneNumber, {
      onSuccess: (data: LoginResponse) => {
        if (errorMessage) setErrorMessage("");

        const { unLoginType } = data;

        if (unLoginType === "Member") {
          return openApp(market);
        } else {
          setTimeLeft(CountDownTime);
          setShowSmsDrawer(true);
        }
      },
      onError: (error: any) => {
        setErrorMessage("Login failed with code " + error.status);
      },
    });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const marketParam = searchParams.get("market");
    const isValidMarketParam =
      marketParam && ["HK", "KR", "TW"].includes(marketParam as MarketType);
    const marketValue = isValidMarketParam ? (marketParam as MarketType) : "TW";

    setMarket(marketValue);
    changeLanguageByMarket(marketValue, setLanguage);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!language) return;

    importFont(language, setFontLoaded);
  }, [language]);

  if (market !== "TW" && market !== "HK" && market !== "KR")
    return <div>This page is not available in your region</div>;

  if (!language || !fontLoaded) return <Loader />;

  return (
    <div className="relative flex min-h-screen w-full justify-center bg-pink1">
      <div className="relative flex min-h-screen w-full min-w-[320px] max-w-[480px] flex-col overflow-x-hidden">
        <img
          src={dweLogo}
          alt="dweLogo"
          className="relative z-[2] mx-4 mt-2 w-32 tablet:mx-8 tablet:mt-4 tablet:w-[200px]"
        />

        <div className="relative z-[2] mx-10 mt-4 tablet:mx-8 tablet:mt-10">
          <img
            src={playText}
            alt="play"
            className="w-[136px] tablet:w-[200px]"
          />
          <Text
            lg
            className="mx-1 mt-3 font-medium leading-[22px] tablet:mx-2 tablet:text-[20px] tablet:font-semibold tablet:leading-[26px]"
          >
            {t("loginSlogan")}
          </Text>
        </div>

        <div className="flex w-full flex-1 flex-col justify-start tablet:justify-center">
          <div className="relative w-full">
            <div className="relative h-[360px] w-full overflow-hidden tablet:h-[450px]">
              <div className="absolute bottom-0 left-1/2 h-[250px] w-[220px] -translate-x-1/2 overflow-hidden tablet:h-[313px] tablet:w-[275px]">
                <img
                  src={eTabWelcome}
                  alt="eTabWelcome"
                  className={cn(
                    "h-full w-auto object-cover transition-all duration-500",
                    isFirstFocused ? "object-left" : "object-right",
                  )}
                />
              </div>

              <img
                src={background}
                alt="background"
                className="absolute left-1/2 z-[1] h-full w-auto -translate-x-1/2 object-cover"
              />
            </div>

            <div className="relative flex w-full flex-col items-center bg-[linear-gradient(9deg,var(--tw-gradient-stops))] from-pink1/30 from-[42%] via-pink2 via-[140%] to-orange to-[246%] px-10 py-8 tablet:min-h-[256px]">
              <PhoneNumberInput
                placeholder={t("mobileNumber")}
                setInputValue={setPhoneNumber}
                errorMessage={errorMessage}
                isFirstFocused={isFirstFocused}
                setIsFirstFocused={setIsFirstFocused}
              />

              {errorMessage ? (
                <Text normal className="mt-1 w-full text-red0">
                  {errorMessage}
                </Text>
              ) : null}

              {market === "HK" ? (
                <Text className="mb-8 mt-2 w-full text-[13px] font-medium text-black6">
                  要體驗APP的澳門家長，請手動填上區號853以收取驗證碼。
                </Text>
              ) : (
                <div className="mb-8" />
              )}

              <PillButton
                disabled={phoneNumber.length === 0}
                text={t("startTrial")}
                className="h-12"
                bgColor="bg-disneyGold"
                onClick={handleSubmit}
                isLoading={mutationForLogin.isLoading}
              />
            </div>
          </div>
        </div>
      </div>

      <SmsDrawer
        phoneNumber={phoneNumber}
        showSmsDrawer={showSmsDrawer}
        setShowSmsDrawer={setShowSmsDrawer}
        smsCode={smsCode}
        setSmsCode={setSmsCode}
        timeLeft={timeLeft}
        setTimeLeft={setTimeLeft}
      />
    </div>
  );
}
